/**
 * Cluster 3 · Costs & affordability
 *
 * Contains: Total cost section, Scholarships & financial support.
 * Extracted verbatim from DegreeDetail.jsx (Feb 2026 refactor).
 */
import { ClusterMember } from "@/components/app/degree/ClusterLayout";
import { Section, DataRow, Ev } from "@/components/app/degree/DegreeDetailBits";
import { AIInterpretationNote } from "@/components/app/AIInterpretationCard";
import { CityGuideLink } from "@/components/app/degree/DegreeInterlinks";
import TotalCourseAndLivingCost from "@/components/app/degree/TotalCourseAndLivingCost";
import MoneyLayerCard from "@/components/app/degree/MoneyLayerCard";
import ISEECalcChip from "@/components/app/degree/ISEECalcChip";
import CrossCountryCostCompare from "@/components/app/degree/CrossCountryCostCompare";
import ContinentalAlternativesCard from "@/components/app/ContinentalAlternativesCard";
import UsFinancialAid from "@/components/app/degree/UsFinancialAid";
import { Card } from "@/components/ui/card";
import { filterScholarshipsByLevel } from "@/utils/scholarshipLevel";
import { UK_HOME_CAP_GBP, UK_CAP_LABEL, UCAS_FEE_LABEL, UK_CAP_SOURCE_URL, homeFeeNote, intlFeeNote, publishedSubtotalNote, conditionalFeeNote, feesUnverifiedNote, inheritedExtras, INHERITED_EXTRA_NOTE, FIRST_YEAR_TOTAL_LABEL } from "@/lib/feeYear";
import { courseTuitionUsd } from "@/lib/courseTuition";

export default function Cluster3Costs({ ctx }) {
  const {
    d, uni, c, ai, isHesa, isScorecard,
    scholarshipHasData, totalDegree, knownTuition, fyOnly,
    format, currency,
  } = ctx;
  return (
    <ClusterMember cluster={3}>
      <Section id="cost" title="Total cost">
        <AIInterpretationNote
          interpretation={ai.interpretation}
          sectionKey="cost"
          degreeId={d.id}
          dataAvailable={
            courseTuitionUsd(d) != null ||
            courseTuitionUsd(d, "tuition_domestic_annual_usd") != null ||
            d.cost?.tuition_intl_annual_gbp != null ||
            d.cost?.tuition_domestic_annual_gbp != null ||
            uni?.tuition_intl_annual_usd != null ||
            uni?.tuition_intl_annual_gbp != null ||
            uni?.tuition_domestic_annual_gbp != null
          }
        />
        <TotalCourseAndLivingCost degreeId={d.id} />

        <MoneyLayerCard degree={d} />
        {(d.mandatory_semester_fee_usd != null || d.eu_only_free) && (
          <div className="mb-5 p-4 bg-emerald-50/60 border border-emerald-200 rounded-md text-sm text-emerald-900" data-testid="degree-semester-fee-callout">
            <div className="font-semibold flex items-center gap-2">
              {c.tuition_intl_annual_usd === 0 && !d.eu_only_free
                ? "Tuition is free — but there's a mandatory semester fee."
                : d.eu_only_free
                  ? "Free for EU/EEA students. Non-EU pays tuition + this fee."
                  : "Mandatory semester fee on top of tuition."}
            </div>
            <div className="mt-1 font-mono">
              {d.mandatory_semester_fee_usd ? `${format(d.mandatory_semester_fee_usd)} / year` : "$0"}
              {d.semester_fee_covers && <span className="text-emerald-800/80"> · {d.semester_fee_covers}</span>}
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-x-8">
          {(() => {
            const isUkHesaCost = isHesa && (c.tuition_intl_annual_usd == null && c.tuition_domestic_annual_usd == null);
            const homeUk = d.fee_outlook?.home_uk;
            const intlFo = d.fee_outlook?.intl;
            const domRow = c.tuition_domestic_annual_usd != null
              ? format(c.tuition_domestic_annual_usd)
              : isUkHesaCost ? `£${(d.fee_outlook?.uk_home_cap_gbp ?? UK_HOME_CAP_GBP).toLocaleString()} (${d.fee_outlook?.uk_cap_label ?? UK_CAP_LABEL})` : "—";
            const intlRow = c.tuition_intl_annual_usd != null
              ? format(c.tuition_intl_annual_usd)
              : isUkHesaCost ? "£17,000-£27,500 (typical range)" : "—";
            const domGbp = c.tuition_domestic_annual_gbp;
            // Stored £ intl fee is the published-year figure: scale it to the 2027/28 estimate.
            const intlGbp = (c.tuition_intl_annual_gbp != null && intlFo?.is_estimate && intlFo.published_usd && intlFo.basis === "growth_rate")
              ? Math.round(c.tuition_intl_annual_gbp * intlFo.estimate_usd / intlFo.published_usd)
              : c.tuition_intl_annual_gbp;
            const domRowFinal = (isHesa && domGbp != null && currency !== "GBP")
              ? <>{domRow} <span className="text-stone-500 text-sm">(£{domGbp.toLocaleString()})</span></>
              : domRow;
            const intlRowFinal = (isHesa && intlGbp != null && currency !== "GBP")
              ? <>{intlRow} <span className="text-stone-500 text-sm">(£{intlGbp.toLocaleString()})</span></>
              : intlRow;
            const intlTier = c.tuition_intl_source_tier;
            const domNote = homeFeeNote(homeUk) || intlFeeNote(d.fee_outlook?.home_other, format) || (isUkHesaCost ? (d.fee_outlook?.uk_cap_label ?? UK_CAP_LABEL) : null);
            const tierNote = isHesa ? (intlTier === "estimate" || intlTier === "tier_estimate"
              ? "Tier estimate — check official fee page"
              : intlTier === "curated" ? "Official fee page" : null) : null;
            const intlNote = [intlFeeNote(intlFo, format), tierNote].filter(Boolean).join(" · ") || null;
            const appFeeRow = c.application_fee_usd
              ? format(c.application_fee_usd)
              : isUkHesaCost ? (d.fee_outlook?.ucas_fee_label ?? UCAS_FEE_LABEL) : "—";
            const subtotal = intlFo?.published_subtotal_usd;
            return (
              <>
                {subtotal != null && (
                  <DataRow k={`${intlFo.published_subtotal_year || "Published"} tuition + required fees /yr`} v={format(subtotal)}
                    note={`${publishedSubtotalNote(intlFo, format)} · sourced published figure`}
                    tooltip="The university's own published tuition plus its compulsory fees for that academic year, each counted once. Living costs are not included."
                    testId="cost-published-subtotal" />
                )}
                <DataRow
                  k={subtotal != null && intlFo?.is_estimate ? `Tuition (International) /yr · ${intlFo.estimate_year} estimate` : "Tuition (International) /yr"}
                  v={intlRowFinal}
                  note={intlNote}
                  tooltip="The annual sticker fee an overseas / international student pays. In the UK this is set by each university and varies by course type — classroom courses run cheaper than lab/clinical. Excludes any scholarships or bursaries you might separately secure."
                />
                <DataRow
                  k={subtotal != null && d.fee_outlook?.home_other?.is_estimate ? `Tuition (Home) /yr · ${d.fee_outlook.home_other.estimate_year} estimate` : "Tuition (Home) /yr"}
                  v={domRowFinal}
                  note={domNote}
                  tooltip={`The Home-status annual fee. In England the government caps it (${d.fee_outlook?.uk_cap_label ?? UK_CAP_LABEL}: £${(d.fee_outlook?.uk_home_cap_gbp ?? UK_HOME_CAP_GBP).toLocaleString()} for providers with a TEF award and access plan). Scotland, Wales and Northern Ireland set their own Home fees.`}
                />
                {d.fee_outlook?.intl?.required_fees_usd != null && (
                  <DataRow k="Required fees /yr" v={format(d.fee_outlook.intl.required_fees_usd)}
                    note={[d.fee_outlook.intl.required_fees_label, d.fee_outlook.intl.published_year && `${d.fee_outlook.intl.published_year} fee year`, feesUnverifiedNote(d.fee_outlook.intl) && "listed fees only · complete required fees not verified"].filter(Boolean).join(" · ") || null}
                    tooltip="Compulsory fees charged on top of tuition, counted once in the annual total." />
                )}
                {feesUnverifiedNote(d.fee_outlook?.intl) && d.fee_outlook?.intl?.required_fees_usd == null && (
                  <DataRow k="Required fees /yr" v="Not verified"
                    note="Unknown, not $0 · not included in any total shown here"
                    tooltip="The university's compulsory fees for this year have not been verified, so no total on this page is a complete degree cost."
                    testId="cost-required-fees-not-verified" />
                )}
                {d.mandatory_semester_fee_usd != null && d.fee_outlook?.intl?.required_fees_usd == null && !d.fee_outlook?.intl?.includes_required_fees && !feesUnverifiedNote(d.fee_outlook?.intl) && (
                  <DataRow k="Mandatory semester fee /yr" v={format(d.mandatory_semester_fee_usd)}
                    tooltip="On top of tuition — a per-semester or per-term charge that's compulsory (services, health, technology). Always paid, never optional." />
                )}
                {(d.cost?.conditional_fees || []).map((f, i) => (
                  <DataRow key={`cf-${i}`} k={`${f.label} (if it applies)`} v={format(f.amount_usd)} note={conditionalFeeNote(f)}
                    tooltip="Charged only in the situation shown. Not part of the tuition + required fees figure above, and not added to any total."
                    testId={`cost-conditional-fee-${i}`} />
                ))}
                <DataRow k="Annual fee growth" v={c.annual_fee_growth_pct ? `${c.annual_fee_growth_pct}%` : (isUkHesaCost ? "Set annually by DfE" : "—")}
                  tooltip="Typical year-over-year percentage rise in tuition. Use this to project years 2, 3, 4 of your total cost." />
                <DataRow k="Deposit" v={c.deposit_usd ? format(c.deposit_usd) : (isUkHesaCost ? "Not standard for UK" : "—")}
                  tooltip="Upfront amount paid to accept an offer. Held against tuition and usually refundable if your visa is refused. UK universities rarely charge one." />
                <DataRow k="Application fee" v={appFeeRow}
                  tooltip={`One-off fee to submit an application. UK UCAS charges ${d.fee_outlook?.ucas_fee_label ?? UCAS_FEE_LABEL}, covering up to five choices. Some private/US universities charge $50-$100 per application.`} />
                <DataRow k="Lab/studio fees /yr" note={inheritedExtras(c).lab_studio_fees_usd != null ? INHERITED_EXTRA_NOTE : null} v={c.lab_studio_fees_usd ? format(c.lab_studio_fees_usd) : (isUkHesaCost ? "See course page" : "—")}
                  tooltip="Extra annual charge for lab / studio / workshop access — usually only applies to science, engineering, art, music, or performance courses." />
                <DataRow k="Equipment/software" note={inheritedExtras(c).equipment_software_usd != null ? INHERITED_EXTRA_NOTE : null} v={c.equipment_software_usd ? format(c.equipment_software_usd) : (isUkHesaCost ? "See course page" : "—")}
                  tooltip="Compulsory kit you must buy yourself (drawing tools, laptop spec, DAW licence, DSLR camera). Excludes optional purchases." />
                <DataRow k="Books/materials /yr" note={inheritedExtras(c).books_materials_usd != null ? INHERITED_EXTRA_NOTE : null} v={c.books_materials_usd ? format(c.books_materials_usd) : (isUkHesaCost ? "See course page" : "—")}
                  tooltip="Typical annual book and materials spend. Reading lists average £300-£600/year at UK unis; STEM courses tend lower thanks to digital resources." />
                <DataRow k="Compulsory travel /yr" note={inheritedExtras(c).compulsory_travel_usd != null ? INHERITED_EXTRA_NOTE : null} v={c.compulsory_travel_usd ? format(c.compulsory_travel_usd) : (isUkHesaCost ? "See course page" : "—")}
                  tooltip="Field trips, placement travel, or study-abroad terms that are part of the degree and must be self-funded. Excludes commuting from home." />
                <DataRow k={feesUnverifiedNote(d.fee_outlook?.intl) ? "Partial degree cost (required fees not verified)" : Object.keys(inheritedExtras(c)).length ? "Partial degree cost (excludes additional costs of unverified provenance)" : "Total degree cost"} v={fyOnly ? FIRST_YEAR_TOTAL_LABEL : (totalDegree > 0 || knownTuition) ? format(totalDegree) : (isUkHesaCost ? "See course page for intl total" : "—")}
                  testId="cost-total-degree"
                  tooltip="Sum of International tuition × programme length + mandatory semester fees + lab/studio/equipment/books/travel over all years. Excludes living costs — see the summary card at the top of the page." />
              </>
            );
          })()}
        </div>
        {(typeof d.id === "string" && d.id.startsWith("it-")) && (
          <ISEECalcChip
            maxTuitionUsd={c.tuition_intl_annual_usd ?? uni?.tuition_intl_annual_usd ?? 0}
            isPrivate={d.university_id === "it-bocconi" || d.university_id === "it-luiss"}
          />
        )}
        <CrossCountryCostCompare degreeId={d.id} />
        <Ev ev={c.evidence
          || (isHesa && !c.tuition_domestic_annual_usd ? { source: `GOV.UK · ${d.fee_outlook?.uk_cap_label ?? UK_CAP_LABEL} · UCAS fee ${d.fee_outlook?.ucas_fee_label ?? UCAS_FEE_LABEL}`, url: d.fee_outlook?.home_uk?.source_url ?? UK_CAP_SOURCE_URL, confidence: "verified" } : null)} />
        {c.evidence?.note && (
          <p className="text-[11px] text-stone-500 mt-1 leading-snug" data-testid="cost-evidence-note">{c.evidence.note}</p>
        )}
        <ContinentalAlternativesCard degreeId={d.id} />
      </Section>

      {scholarshipHasData && (
      <Section id="scholarships" title="Scholarships & financial support">
        {(() => {
          const degreeList = filterScholarshipsByLevel(
            d.scholarships || [],
            d.qualification,
            d.duration_years,
          );
          const uniList = filterScholarshipsByLevel(
            uni?.scholarships_available || [],
            d.qualification,
            d.duration_years,
          );
          const items = degreeList.length ? degreeList : uniList;
          const fromUni = !degreeList.length && uniList.length > 0;

          if (!items.length) {
            return isScorecard ? (
              <UsFinancialAid degree={d} uni={uni} />
            ) : (
              <div className="text-sm text-stone-500" data-testid="scholarships-empty">
                No scholarships encoded yet for this course.
              </div>
            );
          }
          return (
            <>
              {fromUni && (
                <div
                  data-testid="scholarships-from-uni-note"
                  className="mb-3 text-[11px] font-underground-sc tracking-wider text-stone-500"
                >
                  Uni-wide awards {uni?.scholarships_evidence?.source_url ? (
                    <a
                      href={uni.scholarships_evidence.source_url}
                      target="_blank"
                      rel="noreferrer"
                      className="underline decoration-dotted hover:text-stone-800"
                    >
                      · source
                    </a>
                  ) : null}
                </div>
              )}
              {items.map((s, i) => (
                <Card key={i} className="p-4 border-stone-200 mb-2" data-testid={`scholarship-card-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-editorial text-lg">{s.name}</div>
                    {s.extracted_at && (
                      <span
                        data-testid={`scholarship-verified-${i}`}
                        title={`Verified against ${s.source_url || "the university page"} on ${new Date(s.extracted_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`}
                        className="shrink-0 inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px] font-underground-sc tracking-wider px-2 py-0.5"
                      >
                        Verified · {new Date(s.extracted_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-stone-700 mt-1">
                    {s.value_usd ? format(s.value_usd) : (s.value_pct_tuition ? `${Math.round(s.value_pct_tuition * 100)}% of tuition` : "Amount TBD")}
                    {" · "}{s.duration_years || 1}y
                    {" · "}{s.auto_or_application === "automatic" || s.auto_or_application === "auto" ? "Automatic" : "By application"}
                    {s.renewable ? " · Renewable" : ""} {s.intl_eligible ? "· International eligible" : ""}
                  </div>
                  {s.eligibility && <div className="text-sm mt-1"><b>Eligibility:</b> {s.eligibility}</div>}
                  {s.deadline && <div className="text-sm"><b>Deadline:</b> {s.deadline}</div>}
                  {s.source_url && !s.evidence && (
                    <div className="text-[10px] font-mono mt-1 text-stone-500">
                      <span className="uppercase tracking-wider">{s.confidence || "verified"}</span>
                      {" · "}
                      <a href={s.source_url} target="_blank" rel="noreferrer" className="underline">source</a>
                    </div>
                  )}
                  <Ev ev={s.evidence} />
                </Card>
              ))}
            </>
          );
        })()}
      </Section>
      )}
      <CityGuideLink city={uni?.city} />
    </ClusterMember>
  );
}
